<template>
  <div class="absolute inset-0 overflow-hidden bg-black">
    <div
      v-if="error && !items.length"
      class="absolute inset-0 z-20 flex items-center justify-center bg-zinc-950 text-center text-white/80"
    >
      <p>{{ error }}</p>
    </div>
    <template v-else-if="items.length">
      <!-- 90° / 270°: same logical viewport + Tailwind rotate as idle chrome (no inner CSS rotate). -->
      <!-- 90° / 270°: logical viewport + Tailwind rotate (same as idle chrome). -->
      <div
        v-if="usesLogicalViewportRotation"
        class="absolute"
        :class="viewportRotationClasses"
      >
        <G1RssStoryWall
          class="absolute inset-0 z-10 size-full min-h-0 min-w-0"
          :items="items"
          :current-index="currentIndex"
          :feed-label="feedLabel"
          :provider-id="providerId"
          :visible="visible"
          :layout-aspect-ratio="layoutAspectRatio"
        />
      </div>
      <!-- 0° / 180°: fill the playback viewport; g1-wall mode from screen shape, not frame.layout letterboxing. -->
      <div
        v-else
        class="absolute inset-0"
      >
        <G1RssStoryWall
          class="absolute inset-0 z-10 size-full min-h-0 min-w-0"
          :items="items"
          :current-index="currentIndex"
          :feed-label="feedLabel"
          :provider-id="providerId"
          :visible="visible"
          :layout-aspect-ratio="layoutAspectRatio"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { TRotation } from '@/components/VFrame.vue'
import type { IQueueFrame } from '@/types'
import { getRssDirectoryEntry } from '@lucimark/shared/helpers/rssDirectoryCatalog'
import { computeRssDirectoryFrameDuration } from '@lucimark/shared/helpers/rssDirectoryFrame'
import { resolveRssMaxAgeSec } from '@lucimark/shared/helpers/rssDirectoryMaxAge'
import {
  countStoryWindows,
  filterRssItemsByMaxAge,
  resolveRssStoryWindowItems
} from '@lucimark/shared/helpers/rssFeedStory'
import {
  rssWallLayoutAspectFromViewport,
  usesRssWallLogicalViewportRotation
} from '@lucimark/shared/helpers/rssWallLayoutAspect'
import { computed, onBeforeUnmount, ref, toRefs, unref, watch } from 'vue'
/** v1: all providers use the G1 story wall layout; see RSS_DIRECTORY_PROVIDERS.rendererId for future skins. */
import G1RssStoryWall from '@/components/rss/G1RssStoryWall.vue'
import usePlayer from '@/composables/usePlayer'
import { usePlayerViewportRotationClasses } from '@/composables/usePlayerViewportRotationClasses'
import {
  fetchRssDirectoryEntry,
  peekCachedRssDirectoryEntry,
  rssDirectoryEntryResponseError
} from '@/services/RssDirectoryFeedService'
import {
  resetRssStoryWindowSession,
  rssStoryWindowIndexFor,
  rssStoryWindowLastAdvanceMs,
  setRssStoryWindowIndex,
  setRssStoryWindowLastAdvanceMs
} from '@/services/rssStoryWindowSession'

const props = defineProps<{
  frame: IQueueFrame
  rotation?: TRotation
  visible: boolean
}>()

function resetStoryWindowForEntry(entryId: string): void {
  resetRssStoryWindowSession(entryId)
}

function expectedFrameDurationSec(): number {
  const ref = rssRef.value
  if (!ref) {
    return slideCount.value * slideDurationSec.value
  }
  return computeRssDirectoryFrameDuration(ref, catalogEntry.value)
}

function minMsBetweenWindowAdvances(): number {
  const sec = Math.max(frame.value.duration ?? 0, expectedFrameDurationSec())
  return Math.max(2000, Math.floor(sec * 1000 * 0.75))
}

function resolveStoryWindowIndexForPlayback(
  entryId: string,
  windowCount: number,
  watchContext?: { prevVis?: boolean }
): number {
  if (windowCount <= 1 || !visible.value) {
    return rssStoryWindowIndexFor(entryId)
  }

  const now = Date.now()
  const lastAdv = rssStoryWindowLastAdvanceMs(entryId)
  const msSinceLastAdv = lastAdv == null ? null : now - lastAdv
  const minGapMs = minMsBetweenWindowAdvances()
  const becameVisible = watchContext?.prevVis === false
  const shouldAdvanceForLoop = lastAdv == null
    || becameVisible
    || (msSinceLastAdv != null && msSinceLastAdv >= minGapMs)

  if (!shouldAdvanceForLoop) {
    return rssStoryWindowIndexFor(entryId)
  }

  if (lastAdv == null) {
    setRssStoryWindowIndex(entryId, 0)
    setRssStoryWindowLastAdvanceMs(entryId, now)
    return 0
  }

  const prev = rssStoryWindowIndexFor(entryId)
  const next = (prev + 1) % windowCount
  setRssStoryWindowIndex(entryId, next)
  setRssStoryWindowLastAdvanceMs(entryId, now)
  return next
}

const { frame, visible, rotation } = toRefs(props)
const { state } = usePlayer()
const { viewportRotationClasses } = usePlayerViewportRotationClasses()

const effectiveRotation = computed(() => unref(rotation) ?? state.screenRotation)

const usesLogicalViewportRotation = computed(() =>
  usesRssWallLogicalViewportRotation(effectiveRotation.value)
)

const layoutAspectRatio = computed(() => {
  const { width, height } = state.screenSize
  return rssWallLayoutAspectFromViewport(width, height, effectiveRotation.value)
})

const error = ref<string | null>(null)
const items = ref<import('@lucimark/shared/models/rssDirectory').TRssParsedItem[]>([])
const currentIndex = ref(0)
let slideTimer: ReturnType<typeof setInterval> | null = null

const rssRef = computed(() => frame.value.rssDirectoryRef)
const catalogEntry = computed(() => {
  const id = rssRef.value?.entryId
  return id ? getRssDirectoryEntry(id) : undefined
})

const feedLabel = computed(() => feedLabelFromFetch.value)

const feedLabelFromFetch = ref('G1')
const providerIdFromFetch = ref('g1')

const providerId = computed(() => providerIdFromFetch.value)

const slideDurationSec = computed(() => {
  return rssRef.value?.overrides?.slideDurationSec
    ?? catalogEntry.value?.defaults.slideDurationSec
    ?? 10
})

const slideCount = computed(() => {
  return rssRef.value?.overrides?.slideCount
    ?? catalogEntry.value?.defaults.slideCount
    ?? 3
})

const maxAgeSec = computed(() => {
  const ref = rssRef.value
  if (!ref) {
    return 0
  }
  return resolveRssMaxAgeSec(ref, catalogEntry.value)
})

onBeforeUnmount(() => {
  clearSlideTimer()
})

function clearSlideTimer() {
  if (slideTimer != null) {
    clearInterval(slideTimer)
    slideTimer = null
  }
}

function startSlideTimer() {
  clearSlideTimer()
  if (!visible.value || items.value.length <= 1) {
    return
  }
  slideTimer = setInterval(() => {
    currentIndex.value = (currentIndex.value + 1) % items.value.length
  }, slideDurationSec.value * 1000)
}

function restartSlideTimerIfVisible(resetTimer: boolean) {
  if (!visible.value) {
    return
  }
  if (!resetTimer && slideTimer != null) {
    return
  }
  startSlideTimer()
}

async function refreshFeed(watchContext?: { prevVis?: boolean }) {
  const entryId = rssRef.value?.entryId
  if (!entryId) {
    error.value = 'RSS entry missing'
    return
  }
  try {
    const cached = peekCachedRssDirectoryEntry(entryId)
    const payload = cached ?? await fetchRssDirectoryEntry(entryId)
    feedLabelFromFetch.value = payload.label || catalogEntry.value?.label || 'G1'
    providerIdFromFetch.value = payload.provider || catalogEntry.value?.provider || 'g1'
    const filtered = filterRssItemsByMaxAge(payload.items, maxAgeSec.value)
    const windowCount = countStoryWindows(filtered.length, slideCount.value)
    const prevWindowIndex = rssStoryWindowIndexFor(entryId)
    const windowIndex = resolveStoryWindowIndexForPlayback(entryId, windowCount, watchContext)
    const { items: windowItems } = resolveRssStoryWindowItems(
      filtered,
      slideCount.value,
      windowIndex
    )
    const loadError = rssDirectoryEntryResponseError(payload)
    if (windowItems.length === 0) {
      items.value = []
      error.value = loadError ?? 'No RSS stories available'
      clearSlideTimer()
      return
    }
    const windowChanged = windowIndex !== prevWindowIndex
    items.value = windowItems
    if (windowChanged) {
      currentIndex.value = 0
    }
    error.value = null
    restartSlideTimerIfVisible(windowChanged || slideTimer == null)
  }
  catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load feed'
    if (items.value.length === 0) {
      const cached = peekCachedRssDirectoryEntry(entryId)
      if (cached) {
        items.value = cached.items.slice(0, slideCount.value)
      }
    }
    restartSlideTimerIfVisible(true)
  }
}

watch([() => rssRef.value?.entryId, visible], ([entryId, vis], [prevEntryId, prevVis]) => {
  const ref = rssRef.value
  if (entryId && prevEntryId != null && prevEntryId !== entryId) {
    resetStoryWindowForEntry(prevEntryId)
    if (entryId) {
      resetStoryWindowForEntry(entryId)
    }
  }
  if (!vis) {
    clearSlideTimer()
  }
  void refreshFeed({ prevVis })
}, { immediate: true })

watch(slideDurationSec, () => {
  if (visible.value) {
    startSlideTimer()
  }
})
</script>
